import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { usePortalAuth } from "../../components/PortalAuthContext";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { API_BASE } from "../../components/AuthContext";

export default function PortalLogin() {
  const { login, formatApiError } = usePortalAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(new URLSearchParams(loc.search).get("forgot") === "1");
  const [sent, setSent] = useState("");

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API_BASE}/portal/auth/forgot`, { email: email.toLowerCase() });
      setSent(r.data.message);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email.toLowerCase(), password);
      const next = new URLSearchParams(loc.search).get("next") || "/portal";
      navigate(next);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--canvas)" }} data-testid="portal-login-page">
      <div className="w-full max-w-md">
        <div className="overline mb-2">Client portal</div>
        <h1 className="font-heading text-3xl mb-2" style={{ fontWeight: 500 }}>
          Welcome <em className="italic" style={{ color: "var(--accent)" }}>back</em>
        </h1>
        <p className="text-[var(--ink-2)] mb-8 text-sm">
          Sign in with your email and password — or use the magic link in your email.
        </p>
        {forgot ? (
          <form key="forgot" onSubmit={sendReset} className="card p-6 space-y-4" data-testid="portal-forgot-form">
            <div className="text-[13px] text-[var(--ink-2)]">Enter your email and we'll send you a link to choose a new password.</div>
            <input type="email" value={email} required onChange={(e) => setEmail(e.target.value)} data-testid="portal-forgot-email"
                   className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
            {sent && <div className="text-[13px] text-[var(--moss)]" data-testid="portal-forgot-sent">{sent}</div>}
            {err && <div className="text-[13px] text-[var(--accent)]" data-testid="portal-forgot-error">{err}</div>}
            <button type="submit" disabled={busy || !!sent} className="btn-primary w-full justify-center" data-testid="portal-forgot-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} Send reset link
            </button>
            <button type="button" onClick={() => { setForgot(false); setSent(""); setErr(""); }} className="text-[12px] text-[var(--accent)] hover:underline w-full" data-testid="portal-forgot-back">Back to sign in</button>
          </form>
        ) : (
        <form key="login" onSubmit={submit} className="card p-6 space-y-4" data-testid="portal-login-form">
          <div>
            <label className="overline block mb-1.5">Email</label>
            <input type="email" value={email} required onChange={(e) => setEmail(e.target.value)}
                   data-testid="portal-login-email"
                   className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <div>
            <label className="overline block mb-1.5">Password</label>
            <input type="password" value={password} required minLength={8} onChange={(e) => setPassword(e.target.value)}
                   data-testid="portal-login-password"
                   className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          {err && <div className="text-[13px] text-[var(--accent)]" data-testid="portal-login-error">{err}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="portal-login-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}{busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" onClick={(e) => { e.preventDefault(); setForgot(true); setErr(""); }} className="text-[12px] text-[var(--accent)] hover:underline w-full" data-testid="portal-forgot-link">Forgot password?</button>
        </form>
        )}
        <p className="text-center text-[12px] text-[var(--ink-3)] mt-6">
          Are you a firm? <Link to="/login" className="text-[var(--accent)] hover:underline">Sign in here</Link>
        </p>
      </div>
    </div>
  );
}
